import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import { Form } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { profileFormSchema } from "./types";
import type { FormValues } from "./types";
import { Card } from "@/components/ui/card";
import { ProfileHeader } from "./ProfileHeader";
import { PersonalInfoFields } from "./PersonalInfoFields";
import { Loader2 } from "lucide-react";
import { ProfileSummary } from "./ProfileSummary";

interface ProfileFormProps {
  profile: any;
}

export function ProfileForm({ profile }: ProfileFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();

  // Parse date_of_birth if it exists and is valid
  const parseDateOfBirth = (dateString: string | null | undefined) => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? undefined : date;
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      full_name: profile?.full_name || "",
      email: profile?.email || "",
      phone: profile?.phone || "",
      date_of_birth: parseDateOfBirth(profile?.date_of_birth),
      address: profile?.address || "",
      occupation: profile?.occupation || "",
      tfn: profile?.tfn || "",
      avatar_url: profile?.avatar_url || "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("No authenticated user found");
      }

      const formattedValues = {
        ...values,
        date_of_birth: values.date_of_birth ? values.date_of_birth.toISOString().split('T')[0] : null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('profiles')
        .update(formattedValues)
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <div className="border-b pb-4">
            <h1 className="text-2xl font-semibold">Profile Settings</h1>
            <p className="text-sm text-muted-foreground">
              Manage your personal information and account preferences
            </p>
          </div>
          
          <Card className="p-6">
            <ProfileHeader 
              profile={profile}
              isEditing={isEditing}
              isLoading={isLoading}
              setIsEditing={setIsEditing}
              onSubmit={form.handleSubmit(onSubmit)}
            />

            <div className="mt-8 space-y-6 animate-fade-up">
              <PersonalInfoFields form={form} isEditing={isEditing} />
            </div>
          </Card>

          <ProfileSummary 
            profile={{
              ...profile,
              last_login: profile.last_login ? new Date(profile.last_login) : null,
              created_at: profile.created_at ? new Date(profile.created_at) : null,
              updated_at: profile.updated_at ? new Date(profile.updated_at) : null
            }} 
          />
        </div>
      </form>
    </Form>
  );
}